import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Search, Users, UserCheck, Star, Sun, Moon, Settings, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadForm from "./LeadForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProductPricing from "./ProductPricing";
import axios from "axios";
import BrandManager from "./BrandManager";
import JobManager from "./JobManager";
import Downloads from "./Downloads";
import { toast } from "sonner";

const TradeDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("lead-form");
  const [leads, setLeads] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [viewingLead, setViewingLead] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [isLightMode, setIsLightMode] = useState(false);
  const [draftProducts, setDraftProducts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("trade_token");
    if (!token) {
      navigate("/trade/login");
      return;
    }
    fetchData(token);
  }, [navigate]);

  const toggleTheme = () => {
      setIsLightMode(!isLightMode);
  };

  const fetchData = async (token) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
        const leadsRes = await axios.get(`${BACKEND_URL}/api/leads`, config);
        setLeads(leadsRes.data);

        try {
            const usersRes = await axios.get(`${BACKEND_URL}/api/auth/pending-users`, config);
            setPendingUsers(usersRes.data);
            setIsAdmin(true);
        } catch (err) {
            setIsAdmin(false);
        }

    } catch (error) {
        console.error("Error loading dashboard data", error);
    }
  };

  const handleExportLeads = () => {
      if (leads.length === 0) {
          toast.error("No leads to export");
          return;
      }

      // Convert leads to CSV
      const headers = ["Date", "Name", "Company", "Email", "Phone", "Agent", "Type", "Interests"];
      const csvRows = [headers.join(",")];

      leads.forEach(lead => {
          const row = [
              new Date(lead.timestamp).toLocaleDateString(),
              `"${lead.name || ''}"`,
              `"${lead.company || ''}"`,
              `"${lead.email || ''}"`,
              `"${lead.phone || ''}"`,
              `"${lead.agent_username || 'System'}"`,
              `"${(lead.buyer_type || []).join(';')}"`,
              `"${(lead.interested_products || []).map(p => p.sku).join(';')}"`
          ];
          csvRows.push(row.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };
  const refreshLeads = async () => {
        const token = localStorage.getItem("trade_token");
        if (token) fetchData(token);
   };

  const handleApproveUser = async (username) => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("trade_token");
      try {
          await axios.post(`${BACKEND_URL}/api/auth/approve-user?username=${username}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("User Approved", { description: `${username} can now log in.` });
          fetchData(token); 
      } catch (error) {
          toast.error("Approval Failed");
      }
  };
  const handleProceedToLeadForm = () => {
      setActiveTab("lead-form");
      toast.info("Draft transferred to New Lead form", { description: "Please complete the lead details." });
  };

  const handleLogout = () => {
    localStorage.removeItem("trade_token");
    navigate("/trade/login");
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isLightMode ? 'light-theme bg-gray-50 text-gray-900' : 'bg-dreibach-black text-white'}`}>
      {/* Admin Header */}
      <div className={`border-b py-4 px-6 flex justify-between items-center sticky top-0 z-50 ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`}>
         <div className="flex items-center gap-4">
            <span className={`text-xs font-bold px-2 py-1 uppercase tracking-widest ${isLightMode ? 'bg-dreibach-teal text-white' : 'bg-dreibach-teal text-white'}`}>
                {isAdmin ? "Admin Portal" : "Sales Portal"}
            </span>
            <h1 className={`text-xl font-black uppercase tracking-wide ${isLightMode ? 'text-gray-900' : 'text-white'}`}>Dreibach Trade</h1>
         </div>
         <div className="flex items-center gap-4">
             <Button variant="ghost" size="icon" onClick={toggleTheme} className={isLightMode ? 'text-gray-500 hover:text-black' : 'text-gray-400 hover:text-white hover:bg-white/10'}>
                 {isLightMode ? <Moon size={18} /> : <Sun size={18} />}
             </Button>
             {isAdmin && <span className={`text-xs uppercase font-bold tracking-widest hidden md:inline ${isLightMode ? 'text-gray-500' : 'text-gray-500'}`}>Logged in as Administrator</span>}
             <Button variant="ghost" onClick={handleLogout} className={`gap-2 ${isLightMode ? 'text-gray-500 hover:text-black hover:bg-gray-100' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}>
                <LogOut size={16} /> Logout
             </Button>
         </div>
      </div>

      <div className="container mx-auto px-6 py-8">
         {/* RECENT LEADS WIDGET */}
         <div className="mb-8">
             <h3 className={`text-sm font-bold uppercase tracking-widest mb-4 ${isLightMode ? 'text-gray-500' : 'text-gray-500'}`}>Recent Activity (Select to Activate)</h3>
             <div className="grid grid-cols-1 gap-4">
                 {/* Replaced Grid with Dropdown */}
                 <Select 
                    value={activeLead?.id || "none"} 
                    onValueChange={(val) => {
                        if (val === "none") {
                            setActiveLead(null);
                            toast.info("Lead Deactivated");
                        } else {
                            const lead = leads.find(l => l.id === val);
                            setActiveLead(lead);
                            toast.success("Lead Activated", { description: lead.company });
                        }
                    }}
                 >
                    <SelectTrigger className={`w-full md:w-96 rounded-none h-12 border ${isLightMode ? 'bg-white border-gray-300 text-gray-900' : 'bg-black/50 border-white/10 text-white'}`}>
                        <SelectValue placeholder="Select a lead to manage..." />
                    </SelectTrigger>
                    <SelectContent className={`${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
                        <SelectItem value="none">-- Deactivate Current Lead --</SelectItem>
                        {leads.map(lead => (
                            <SelectItem key={lead.id} value={lead.id}>
                                {lead.company} ({lead.name}) - {new Date(lead.timestamp).toLocaleDateString()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                 </Select>
                 {leads.length === 0 && <div className="text-gray-500 text-xs italic p-4">No recent leads found.</div>}
             </div>
         </div>

         <Tabs defaultValue="lead-form" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={`border p-1 w-full justify-start h-auto rounded-none mb-8 flex-wrap ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`}>
               <TabsTrigger value="lead-form" className="data-[state=active]:bg-dreibach-teal data-[state=active]:text-white text-gray-400 uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2">
                  <LayoutDashboard size={16} /> New Lead
               </TabsTrigger>
               
               <TabsTrigger value="price-search" className="data-[state=active]:bg-dreibach-teal data-[state=active]:text-white text-gray-400 uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2">
                  <Search size={16} /> Product Pricing
               </TabsTrigger>

               <TabsTrigger value="leads-list" className="data-[state=active]:bg-dreibach-teal data-[state=active]:text-white text-gray-400 uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2">
                  <Users size={16} /> View All Leads
               <TabsTrigger value="downloads" className="data-[state=active]:bg-dreibach-teal data-[state=active]:text-white text-gray-400 uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2">
                  <Download size={16} /> Downloads
               </TabsTrigger>
               </TabsTrigger>
               
               {isAdmin && (
                   <TabsTrigger value="user-approvals" className="data-[state=active]:bg-dreibach-teal data-[state=active]:text-white text-gray-400 uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2">
                      <UserCheck size={16} /> Approvals ({pendingUsers.length})
                   </TabsTrigger>
               )}
               {isAdmin && (
                   <DropdownMenu>
                       <DropdownMenuTrigger asChild>
                           <Button 
                               variant="outline"
                               className={`uppercase font-bold tracking-widest px-6 py-3 rounded-none gap-2 border-0 ${activeTab === 'brand-manager' || activeTab === 'job-manager' ? 'bg-dreibach-teal text-white hover:bg-dreibach-teal hover:text-white' : 'bg-transparent text-gray-400 hover:bg-transparent hover:text-white'}`}
                           >
                               <Settings size={16} /> Admin
                           </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className={`rounded-none border-white/10 ${isLightMode ? 'bg-white' : 'bg-dreibach-card'}`}>
                           <DropdownMenuItem onClick={() => setActiveTab("brand-manager")} className="uppercase font-bold tracking-wider text-xs cursor-pointer gap-2">
                               <Star size={14} /> Brands
                           </DropdownMenuItem>
                           <DropdownMenuItem onClick={() => setActiveTab("job-manager")} className="uppercase font-bold tracking-wider text-xs cursor-pointer gap-2">
                               <Users size={14} /> Careers
                           </DropdownMenuItem>
                       </DropdownMenuContent>
                   </DropdownMenu>
               )}
            </TabsList>

            {/* TAB: NEW LEAD */}
            <TabsContent value="lead-form" className="animate-in fade-in-50 duration-300">
               <LeadForm 
                  onSuccess={() => {
                      refreshLeads();
                      setDraftProducts([]); // Clear draft on success
                  }} 
                  isLightMode={isLightMode} 
                  draftProducts={draftProducts}
               />
            </TabsContent>

            {/* TAB: PRODUCT PRICING */}
            <TabsContent value="price-search" className="animate-in fade-in-50 duration-300">
               <ProductPricing 
                  activeLead={activeLead} 
                  isLightMode={isLightMode} 
                  draftProducts={draftProducts}
                  setDraftProducts={setDraftProducts}
                  onCreateLead={handleProceedToLeadForm}
               />
            </TabsContent>

            {/* TAB: DOWNLOADS */}
            <TabsContent value="downloads" className="animate-in fade-in-50 duration-300">
               <Downloads isLightMode={isLightMode} />
            </TabsContent>

            {/* TAB: VIEW LEADS */}
            <TabsContent value="leads-list" className="animate-in fade-in-50 duration-300">
               <Card className={`border ${isLightMode ? 'bg-white border-gray-200 text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
                  <CardHeader>
                     <CardTitle className="uppercase tracking-widest">All Submitted Leads</CardTitle>
                  </CardHeader>
                  <CardContent>
                     <div className="flex justify-end mb-4">
                        <Button 
                            onClick={handleExportLeads} 
                            variant="outline" 
                            className={`border ${isLightMode ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-white/20 text-gray-400 hover:text-white hover:bg-white/5'} rounded-sm`}
                        >
                            Export CSV
                        </Button>
                     </div>
                     <Table>
                        <TableHeader>
                           <TableRow className={`border-b ${isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}`}>
                              <TableHead className="text-dreibach-teal uppercase text-xs">Date</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Name</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Company</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Email</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Agent</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Type</TableHead>
                              <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Interests</TableHead>
                           </TableRow>
                        </TableHeader>
                        <TableBody>
                           {leads.map((lead) => (
                              <TableRow 
                                key={lead.id} 
                                className={`border-b cursor-pointer ${isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}`}
                                onClick={() => setViewingLead(lead)}
                              >
                                 <TableCell className="text-gray-400 text-xs">
                                    {new Date(lead.timestamp).toLocaleDateString()}
                                 </TableCell>
                                 <TableCell className="font-bold">{lead.name}</TableCell>
                                 <TableCell>{lead.company}</TableCell>
                                 <TableCell className="text-gray-400">{lead.email}</TableCell>
                                 <TableCell className="text-gray-500 text-xs uppercase">{lead.agent_username || 'System'}</TableCell>
                                 <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                        {lead.buyer_type.map(t => <Badge key={t} variant="outline" className={`text-[10px] ${isLightMode ? 'border-gray-300 text-gray-600' : 'border-white/20 text-gray-300'}`}>{t}</Badge>)}
                                    </div>
                                 </TableCell>
                                 <TableCell>
      <Dialog open={!!viewingLead} onOpenChange={(open) => !open && setViewingLead(null)}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
          <DialogHeader>
            <DialogTitle>Lead Details: {viewingLead?.company}</DialogTitle>
          </DialogHeader>
          
          {viewingLead && (
              <div className="space-y-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                      <div>
                          <label className="text-xs uppercase font-bold text-gray-500">Contact</label>
                          <p className="font-bold text-lg">{viewingLead.name}</p>
                          <p className="text-sm">{viewingLead.job_title}</p>
                      </div>
                      <div>
                          <label className="text-xs uppercase font-bold text-gray-500">Details</label>
                          <p className="text-sm">{viewingLead.email}</p>
                          <p className="text-sm">{viewingLead.phone}</p>
                          <p className="text-sm">{viewingLead.country}</p>
                          {viewingLead.website && <a href={viewingLead.website} target="_blank" rel="noreferrer" className="text-xs text-dreibach-teal hover:underline">{viewingLead.website}</a>}
                      </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-white/10 pt-4">
                      <label className="text-xs uppercase font-bold text-gray-500 mb-2 block">Business Profile</label>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                              <span className="text-gray-500">Type:</span> {viewingLead.buyer_type.join(", ")}
                          </div>
                          <div>
                              <span className="text-gray-500">Channels:</span> {viewingLead.sales_channels.join(", ")}
                          </div>
                          <div>
                              <span className="text-gray-500">Stores:</span> {viewingLead.has_physical_stores ? `Yes (${viewingLead.store_count})` : "No"}
                          </div>
                          <div>
                              <span className="text-gray-500">Territories:</span> {viewingLead.territories.join(", ")}
                          </div>
                      </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-white/10 pt-4">
                      <label className="text-xs uppercase font-bold text-gray-500 mb-2 block">Interests</label>
                      <div className="flex gap-2 flex-wrap mb-4">
                          {viewingLead.brands_interest.map(b => <Badge key={b} className="bg-dreibach-teal text-white">{b}</Badge>)}
                      </div>
                      
                      {viewingLead.interested_products?.length > 0 && (
                          <div className={`mt-4 p-3 rounded-sm ${isLightMode ? 'bg-gray-100 text-gray-900' : 'bg-white/10 text-white'}`}>
                              <p className="text-xs font-bold mb-2 uppercase tracking-wide">Selected SKUs ({viewingLead.interested_products.length})</p>
                              <ul className="text-xs space-y-2">
                                  {viewingLead.interested_products.map((p, i) => (
                                      <li key={i} className="flex justify-between">
                                          <span className={`font-mono ${isLightMode ? 'text-gray-800' : 'text-gray-300'}`}>{p.sku} - {p.title}</span>
                                          <span className={`font-bold ${isLightMode ? 'text-green-700' : 'text-green-400'}`}>
                                              {p.special_price ? `£${p.special_price} (Special)` : p.price_point} 
                                              {p.quantity > 1 && ` x${p.quantity}`}
                                          </span>
                                      </li>
                                  ))}
                              </ul>
                          </div>
                      )}
                      
                      <div className="mt-4 text-sm">
                          <p><span className="text-gray-500">Vol:</span> {viewingLead.estimated_volume}</p>
                          <p><span className="text-gray-500">Timing:</span> {viewingLead.order_timing}</p>
                      </div>
                  </div>

                  {viewingLead.notes && (
                      <div className="border-t border-gray-200 dark:border-white/10 pt-4">
                          <label className="text-xs uppercase font-bold text-gray-500 mb-2 block">Notes</label>
                          <p className="text-sm bg-gray-50 dark:bg-black/20 p-3 rounded-sm whitespace-pre-wrap">{viewingLead.notes}</p>
                      </div>
                  )}
              </div>
          )}
        </DialogContent>
      </Dialog>
                                     <span className="text-xs text-gray-500">{lead.interested_products?.length || 0} Products</span>
                                 </TableCell>
                              </TableRow>
                           ))}
                           {leads.length === 0 && (
                               <TableRow>
                                   <TableCell colSpan={7} className="text-center py-8 text-gray-500">No leads submitted yet.</TableCell>
                               </TableRow>
                           )}
                        </TableBody>
                     </Table>
                  </CardContent>
               </Card>
            </TabsContent>

            {/* TAB: BRAND MANAGER (ADMIN) */}
            {isAdmin && (
                <TabsContent value="brand-manager" className="animate-in fade-in-50 duration-300">
                   <BrandManager isLightMode={isLightMode} />
                </TabsContent>
            )}

            {/* TAB: JOB MANAGER (ADMIN) */}
            {isAdmin && (
                <TabsContent value="job-manager" className="animate-in fade-in-50 duration-300">
                   <JobManager isLightMode={isLightMode} />
                </TabsContent>
            )}

            {/* TAB: USER APPROVALS */}
            {isAdmin && (
                <TabsContent value="user-approvals" className="animate-in fade-in-50 duration-300">
                   <Card className={`border ${isLightMode ? 'bg-white border-gray-200 text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
                      <CardHeader>
                         <CardTitle className="uppercase tracking-widest">Pending Access Requests</CardTitle>
                      </CardHeader>
                      <CardContent>
                         <Table>
                            <TableHeader>
                               <TableRow className={`border-b ${isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}`}>
                                  <TableHead className={`uppercase text-xs ${isLightMode ? 'text-gray-700' : 'text-white'}`}>Username</TableHead>
                                  <TableHead className="text-right uppercase text-xs">Action</TableHead>
                               </TableRow>
                            </TableHeader>
                            <TableBody>
                               {pendingUsers.map((user) => (
                                  <TableRow key={user.username} className={`border-b ${isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}`}>
                                     <TableCell className="font-bold text-lg">{user.username}</TableCell>
                                     <TableCell className="text-right">
                                        <Button size="sm" onClick={() => handleApproveUser(user.username)} className="bg-dreibach-teal hover:bg-white hover:text-black uppercase font-bold tracking-wider">
                                            Approve Access
                                        </Button>
                                     </TableCell>
                                  </TableRow>
                               ))}
                               {pendingUsers.length === 0 && (
                                   <TableRow>
                                       <TableCell colSpan={2} className="text-center py-8 text-gray-500">No pending requests.</TableCell>
                                   </TableRow>
                               )}
                            </TableBody>
                         </Table>
                      </CardContent>
                   </Card>
                </TabsContent>
            )}
         </Tabs>
      </div>
    </div>
  );
};

export default TradeDashboard;
